import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  PencilSimple,
  Trash,
  FileText,
  Check,
  X,
  CopyIcon,
  Eye,
  InfoIcon
} from '@phosphor-icons/react';

import Alerts from '../../../shared/components/ui/Alerts';
import Tooltip from '../../../shared/components/ui/Tooltip';
import { useTaskTypes } from '../../../hooks/useTaskTypes';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import RequiredIndicator from '../../../shared/components/ui/RequiredIndicator';
import PaginationFooter from '../../../shared/components/ui/PaginationFooter';
import PageHeader from '../../../shared/components/ui/PageHeader';
import { formService } from '../../../services/formService';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const FormManagement = () => {
  const [forms, setForms] = useState([]);
  const [pageCurrent, setPageCurrent] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [requiredFieldsConfig, setRequiredFieldsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success', origin: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingForm, setViewingForm] = useState(null);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deletingForm, setDeletingForm] = useState(null);
  const [showAlertsContenidoTitle, setShowAlertsContenidoTitle] = useState(false);
  const [showAlertsContenidoDelete, setShowAlertsContenidoDelete] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [emptyFieldNameErrorKeys, setEmptyFieldNameErrorKeys] = useState([]);
  const [touchedFieldNames, setTouchedFieldNames] = useState([]);
  const modalScrollRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'general', // Agregar tipo de formulario
    taskTypes: [],
    fields: []
  });
  // Obtener tipos de tarea desde el API
  const { taskTypes, loading: loadingTaskTypes } = useTaskTypes();

  // Tipos de formulario disponibles
  const formTypes = [
    { value: 'general', label: 'General' },
    { value: 'finalizacion', label: 'Finalización' },
    { value: 'cancelacion', label: 'Cancelación' },
  ];

  // Tipos de campos disponibles
  const fieldTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'textarea', label: 'Área de texto' },
    { value: 'number', label: 'Número' },
    { value: 'email', label: 'Email' },
    { value: 'select', label: 'Selector' },
    { value: 'radio', label: 'Botones de radio' },
    { value: 'checkbox', label: 'Casilla de verificación' },
    { value: 'image', label: 'Imagen' },
    { value: 'date', label: 'Fecha' },
    { value: 'time', label: 'Hora' }
  ];

  const slugifyLabel = (label) =>
    label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_');

  const ensureUniqueFieldName = (baseName, currentIndex) => {
    if (!baseName) return '';
    const existingNames = formData.fields
      .filter((_, idx) => idx !== currentIndex)
      .map(f => f.name)
      .filter(Boolean);

    if (!existingNames.includes(baseName)) return baseName;

    let counter = 1;
    let candidate = `${baseName}-${counter}`;
    while (existingNames.includes(candidate)) {
      counter += 1;
      candidate = `${baseName}-${counter}`;
    }
    return candidate;
  };

  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success', origin: '' });
    }, 3000);
  };

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await formService.getForms({
        pageCurrent,
        pageSize,
        ...(typeFilter ? { type: typeFilter } : {})
      });
      const responses = response.responses || [];
      setForms(responses);
      setTotalItems(Number(response.pagination?.totalItems) || 0);
      setError(null);
    } catch (err) {
      setError('Error al cargar formularios');
      setForms([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequiredFieldsConfig = async () => {
    try {
      const data = await formService.getRequiredFieldsConfig();
      setRequiredFieldsConfig(data);
    } catch (err) {
      console.error('Error al cargar config de campos requeridos', err);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [pageCurrent, pageSize, typeFilter]);

  useEffect(() => {
    fetchRequiredFieldsConfig();
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPageCurrent(0);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageCurrent(0);
  };

  useEffect(() => {
    if (!requiredFieldsConfig || !formData.type) return;
    
    const globalFields = requiredFieldsConfig.globalFields || [];
    const typeFields = (requiredFieldsConfig.formtypes && requiredFieldsConfig.formtypes[formData.type]?.fields) || [];
    const allRequiredFields = [...globalFields, ...typeFields];
    
    if (allRequiredFields.length > 0) {
      const missingFields = allRequiredFields.filter(
        reqField => !formData.fields.some(f => f.id === reqField.id)
      );
      
      if (missingFields.length > 0) {
        setFormData(prev => ({
          ...prev,
          fields: [...prev.fields, ...missingFields]
        }));
      }
    }
  }, [formData.type, requiredFieldsConfig]);

  const resetFormData = () => {
    const globalFields = requiredFieldsConfig?.globalFields || [];
    const typeFields = requiredFieldsConfig?.formtypes?.['general']?.fields || [];
    const initialFields = [...globalFields, ...typeFields];

    setFormData({
      name: '',
      description: '',
      type: 'general',
      taskTypes: [],
      fields: initialFields
    });
    setEmptyFieldNameErrorKeys([]);
    setTouchedFieldNames([]);
  };

  const handleCreateForm = () => {
    resetFormData();
    setShowCreateModal(true);
  };

  const handleEditForm = (form) => {
    // Convertir opciones de campos select/radio a objetos
    const fieldsWithConvertedOptions = form.fields.map(field => {
      if ((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && field.options) {
        return {
          ...field,
          options: convertOptionsToObjects(field.options)
        };
      }
      return field;
    });

    // Migrar IDs antiguos (numéricos) a nombres descriptivos
    const fieldsWithSyncedIds = fieldsWithConvertedOptions.map((field, index) => {
      // Si el ID es numérico (timestamp antiguo) y tiene un name válido, usar el name como id
      const isNumericId = typeof field.id === 'number' || /^\d+$/.test(field.id);
      if (isNumericId && field.name && field.name.trim() !== '') {
        return { ...field, id: field.name };
      }
      // Si no tiene name, generar uno desde el label
      if ((!field.name || field.name.trim() === '') && field.label) {
        const generatedName = generateFieldName(field.label, index);
        return { ...field, id: generatedName, name: generatedName };
      }
      return field;
    });

    setFormData({
      name: form.name,
      description: form.description,
      type: form.type || 'general',
      taskTypes: form.taskTypes,
      fields: fieldsWithSyncedIds
    });
    setEditingForm(form);
    setEmptyFieldNameErrorKeys([]);
    setTouchedFieldNames([]);
    setShowEditModal(true);
  };

  const handleDeleteForm = async () => {

    try {
      await formService.deleteForm(deletingForm.id);
      showNotification('Formulario eliminado correctamente');
      fetchForms();
    } catch (error) {
      showNotification('Error al eliminar formulario', 'error');
    }
  };

  const handleDuplicateForm = async (form) => {
    try {
      if (form.name.trim() === '') {
        showNotification('El nombre del formulario es requerido', 'error');
        return;
      }

      await formService.duplicateForm(form.id, form.name);
      showNotification('Formulario duplicado correctamente');
      fetchForms();
    } catch (error) {
      if (error.message === "Ya existe un formulario con ese nombre") {
        showNotification('Ya existe un formulario con ese nombre', 'error');
      } else {
        showNotification('Error al duplicar formulario', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showNotification('El nombre del formulario es requerido', 'error', 'forms');
      return;
    }

    if (formData.taskTypes.length === 0) {
      showNotification('Debe seleccionar al menos un tipo de tarea', 'error', 'forms');
      return;
    }

    if (formData.fields.length === 0) {
      showNotification('Debe agregar al menos un campo', 'error', 'forms');
      return;
    }

    if (requiredFieldsConfig) {
      const globalFields = requiredFieldsConfig.globalFields || [];
      const typeFields = (requiredFieldsConfig.formtypes && requiredFieldsConfig.formtypes[formData.type]?.fields) || [];
      const allRequiredFields = [...globalFields, ...typeFields];
      
      if (allRequiredFields.length > 0) {
        const missing = allRequiredFields.filter(reqField => !formData.fields.some(f => f.id === reqField.id));
        if (missing.length > 0) {
          showNotification(`Faltan campos obligatorios del sistema: ${missing.map(f => f.label).join(', ')}`, 'error', 'forms');
          return;
        }
      }
    }

    try {
      if (editingForm) {
        await formService.updateForm(editingForm.id, formData);
        showNotification('Formulario actualizado correctamente');
      } else {
        await formService.createForm(formData);
        showNotification('Formulario creado correctamente', 'success');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingForm(null);
      resetFormData();
      fetchForms();
    } catch (error) {
      const errorMessage = error.message || 'Error al guardar formulario';
      const isMissingFieldNameError = errorMessage.includes('Todos los campos deben tener un nombre e id');

      setEmptyFieldNameErrorKeys(
        isMissingFieldNameError
          ? formData.fields
              .map((field, index) => ({
                key: field.id || `field-${index}`,
                hasEmptyName: !field.label?.trim() || !field.name?.trim() || !field.id?.trim()
              }))
              .filter(({ hasEmptyName }) => hasEmptyName)
              .map(({ key }) => key)
          : []
      );
      showNotification(errorMessage, 'error', 'forms');
    }
  };

  const addField = () => {
    const newField = {
      id: `temp_field_${Date.now()}`, // ID temporal hasta que se genere desde el label
      name: '', // Se generará cuando el usuario escriba la etiqueta
      label: '',
      type: 'text',
      required: false,
      options: [],
      placeholder: ''
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  // Función para convertir opciones de string a objeto
  const convertOptionsToObjects = (options) => {
    if (!Array.isArray(options)) return [];
    return options.map(opt => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return opt;
    });
  };

  const updateField = (index, field) => {
    const updatedFields = [...formData.fields];
    updatedFields[index] = field;
    setFormData(prev => ({
      ...prev,
      fields: updatedFields
    }));
  };

  const removeField = (index) => {
    const field = formData.fields[index];
    const globalFields = requiredFieldsConfig?.globalFields || [];
    const typeFields = requiredFieldsConfig?.formtypes?.[formData.type]?.fields || [];
    const allRequiredFields = [...globalFields, ...typeFields];

    if (allRequiredFields.some(f => f.id === field.id)) {
      showNotification('No se puede eliminar un campo obligatorio del sistema', 'error', 'forms');
      return;
    }

    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  // Drag and drop handlers for reordering fields
  const onDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(index));
    } catch (err) {
      // some browsers may throw if dataTransfer not allowed, ignore
    }
    setDraggedIndex(index);
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const onDrop = (e, index) => {
    e.preventDefault();
    const fromStr = e.dataTransfer.getData('text/plain');
    const from = parseInt(fromStr, 10);
    const to = index;
    if (Number.isNaN(from)) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    if (from === to) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newFields = Array.from(formData.fields);
    const [moved] = newFields.splice(from, 1);
    newFields.splice(to, 0, moved);
    setFormData(prev => ({ ...prev, fields: newFields }));
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Recorrido guiado (formsTour): abre el editor de creación en sus pasos y lo descarta al salir.
  // En los pasos de tipo (formType) selecciona ese tipo; sus campos del sistema reemplazan a los del
  // tipo anterior (el efecto de tipo solo agrega, y al volver quedarían como campos configurables).
  // En los pasos de campo (field) agrega un campo configurable de ejemplo si el borrador no tiene uno.
  const [tourStep, setTourStep] = useState(null);
  useGuidedTourStep((step) => {
    if (step?.modal === 'form-create' && !showCreateModal && !showEditModal) handleCreateForm();
    const globalFields = requiredFieldsConfig?.globalFields || [];
    const allTypeFields = Object.values(requiredFieldsConfig?.formtypes || {}).flatMap((type) => type.fields || []);
    const isSystemField = (field) => [...globalFields, ...allTypeFields].some((f) => f.id === field.id);
    if (step?.formType && formData.type !== step.formType) {
      const typeFields = requiredFieldsConfig?.formtypes?.[step.formType]?.fields || [];
      setFormData((prev) => ({
        ...prev,
        type: step.formType,
        fields: [...globalFields, ...typeFields, ...prev.fields.filter((field) => !isSystemField(field))],
      }));
    }
    if (step?.field && formData.fields.every(isSystemField)) addField();
    if (!step?.modal && tourStep?.modal) {
      setShowCreateModal(false);
      resetFormData();
    }
    setTourStep(step);
  });

  const toggleTaskType = (taskType) => {
    setFormData(prev => ({
      ...prev,
      taskTypes: prev.taskTypes.includes(taskType)
        ? prev.taskTypes.filter(t => t !== taskType)
        : [...prev.taskTypes, taskType]
    }));
  };

  // Función para generar nombre de campo automáticamente desde la etiqueta
  const generateFieldName = (label, currentIndex) => {
    if (!label.trim()) return '';

    // Convertir a minúsculas y reemplazar espacios por guiones bajos
    let generatedName = label
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, ''); // Remover caracteres especiales

    // Verificar si ya existe un campo con ese nombre
    const fieldsWithSameName = formData.fields.filter((field, idx) => {
      if (idx === currentIndex) return false; // Excluir el campo actual
      return field.name === generatedName || field.name.match(new RegExp(`^${generatedName}_\\d+$`));
    });

    // Si hay campos con el mismo nombre, agregar número
    if (fieldsWithSameName.length > 0) {
      generatedName = `${generatedName}_${fieldsWithSameName.length + 1}`;
    }

    return generatedName;
  };

  // Scroll to the top of the modal when there is an error notification
  useEffect(() => {
    if (
      (showCreateModal || showEditModal) &&
      notification.show &&
      notification.origin === 'forms' &&
      notification.type === 'error' &&
      modalScrollRef.current
    ) {
      modalScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [notification, showCreateModal, showEditModal]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-content">Cargando formularios...</div>
      </div>
    );
  }

  return (
    <div>
      <div data-tour="forms-header">
        <PageHeader
          title="Formularios"
          subtitle="Crea y gestiona formularios personalizados para diferentes tipos de tareas"
          icon={<FileText size={28} />}
          actionButton={{
            label: 'Crear Formulario',
            icon: <Plus size={20} />,
            onClick: handleCreateForm,
          }}
        />
      </div>

      {/* Notification */}
      {notification.show && notification.origin !== 'forms' && (
        <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-success/12 border border-success text-success' : 'bg-error/12 border border-error text-error'
          }`}>
          {notification.message}
        </div>
      )}

      <div className="mb-4 flex justify-end">
        <select
          data-tour="forms-type-filter"
          value={typeFilter}
          onChange={handleTypeFilterChange}
          aria-label="Filtrar formularios por tipo"
          className="px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
        >
          <option value="">Todos los tipos</option>
          {formTypes.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* Forms List */}
      <div data-tour="forms-list" className="grid gap-4">
        {forms.length === 0 ? (
          <div className="text-center py-8 text-content-muted">
            No hay formularios creados. Crea el primero para comenzar.
          </div>
        ) : (
          forms.map((form) => (
            <div key={form.id} className="bg-surface border border-line rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-content">{form.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${form.type === 'general' ? 'bg-info/12 text-info' : form.type === 'cancelacion' ? 'bg-error/12 text-error' : form.type === 'finalizacion' ? 'bg-success/12 text-success' : 'bg-amber/12 text-amber'}`}>
                      {form.type === 'general' ? 'General' : form.type === 'finalizacion' ? 'Finalización' : form.type === 'cancelacion' ? 'Cancelación' : form.type}
                    </span>
                  </div>
                  {form.description && (
                    <p className="text-content-muted mb-2">{form.description}</p>
                  )}
                  {/*<div clagenssName="mb-2">
                    <span className="text-sm font-medium text-content-muted">Tipos de tarea:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {form.taskTypes.map((taskTypeId) => {
                        const taskType = taskTypes.find(t => t.id === taskTypeId);
                        return (
                          <span key={taskTypeId} className="px-2 py-1 text-xs bg-surface-2 text-content-muted rounded">
                            {taskType?.displayName || taskTypeId}
                          </span>
                        );
                      })}
                    </div>
                  </div>}*/}
                  <div>
                    <span className="text-sm font-medium text-content-muted">Campos: {form.fields.length}</span>
                  </div>
                </div>
                <span data-tour="form-actions" className="inline-flex">
                  <ActionMenu
                    options={[
                      {
                        label: 'Ver',
                        icon: <Eye size={18} />,
                        onClick: () => {
                          setViewingForm(form);
                          setShowViewModal(true);
                        },
                      },
                      {
                        label: 'Editar',
                        icon: <PencilSimple size={18} />,
                        onClick: () => {
                          handleEditForm(form);
                        },
                      },
                      {
                        label: 'Duplicar',
                        icon: <CopyIcon size={18} />,
                        onClick: () => {
                          setShowDuplicate(true);
                          setDuplicateForm(form);
                        },
                      },
                      {
                        label: 'Eliminar',
                        icon: <Trash size={18} />,
                        onClick: () => {
                          setDeletingForm(form);
                          setShowAlertsContenidoTitle('Eliminar Formulario');
                          setShowAlertsContenidoDelete('¿Estás seguro de que quieres eliminar este formulario?');
                          setShowDelete(true);
                        },
                      },
                    ]}
                  />
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {totalItems > 0 && (
        <div className="mt-6">
          <PaginationFooter
            showFooter
            loading={loading}
            pagination={{
              currentPage: pageCurrent + 1,
              totalPages,
              pageSize,
              totalItems,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: (page) => setPageCurrent(page - 1),
              onPageSizeChange: handlePageSizeChange
            }}
          />
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-9998 p-4">
          <div ref={modalScrollRef} data-tour-scroll className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <h2 className="text-xl font-semibold text-content">
                {editingForm ? 'Editar Formulario' : 'Crear Nuevo Formulario'}
              </h2>
            </div>

            <form className="p-6  space-y-6">
              {/* Notification */}
              {notification.show && notification.origin === 'forms' && (
                <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-success/12 border border-success text-success' : 'bg-error/12 border border-error text-error'
                  }`}>
                  {notification.message}
                </div>
              )}
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-content">Información Básica</h3>

                <div data-tour="form-basic-info" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-2">
                      Nombre del Formulario <RequiredIndicator />
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                      placeholder="Ej: Formulario de Instalación"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows="3"
                      className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                      placeholder="Descripción del formulario..."
                    />
                  </div>
                </div>

                <div data-tour="form-type">
                  <label className="block text-sm font-medium text-content-muted mb-2">
                    Tipo de Formulario <RequiredIndicator />
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
                    required
                  >
                    {formTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Task Types */}
              <div data-tour="form-task-types" className="space-y-4">
                <h3 className="text-lg font-medium text-content">Tipos de Tarea <RequiredIndicator /></h3>
                {loadingTaskTypes ? (
                  <div className="text-sm text-content-muted">Cargando tipos de tarea...</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {taskTypes.map((taskType) => (
                      <label key={taskType.name} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.taskTypes.includes(taskType.name)}
                          onChange={() => toggleTaskType(taskType.name)}
                          className="mr-2"
                          value={taskType.name}
                        />
                        <span className="text-sm text-content-muted">{taskType.displayName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Fields */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-content">Campos del Formulario <RequiredIndicator /></h3>
                  {(formData.fields.length === 0) && (
                    <button
                      type="button"
                      data-tour="form-add-field"
                      onClick={addField}
                      className="px-3 py-1 text-sm bg-success text-white rounded hover:bg-success/80"
                    >
                      + Agregar Campo
                    </button>
                  )}
                </div>

                {formData.fields.length === 0 ? (
                  <div className="text-center py-8 text-content-muted">
                    No hay campos agregados. Agrega el primer campo para comenzar.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.fields.map((field, index) => {
                      const globalFields = requiredFieldsConfig?.globalFields || [];
                      const typeFields = requiredFieldsConfig?.formtypes?.[formData.type]?.fields || [];
                      const isSystemField = [...globalFields, ...typeFields].some(f => f.id === field.id);
                      const fieldValidationKey = field.id || `field-${index}`;
                      const hasEmptyName = !field.label?.trim() || !field.name?.trim() || !field.id?.trim();
                      const showEmptyNameError = (emptyFieldNameErrorKeys.includes(fieldValidationKey) || touchedFieldNames.includes(fieldValidationKey)) && hasEmptyName;

                      return (
                      <div
                        key={index}
                        data-tour={isSystemField ? 'form-field-system' : 'form-field'}
                        draggable
                        aria-grabbed={draggedIndex === index}
                        onDragStart={(e) => onDragStart(e, index)}
                        onDragOver={(e) => onDragOver(e, index)}
                        onDrop={(e) => onDrop(e, index)}
                        onDragEnter={() => setDragOverIndex(index)}
                        onDragEnd={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                        className={`border border-line rounded-lg p-4 bg-surface-2 ${dragOverIndex === index ? 'ring-2 ring-amber' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="font-medium text-content cursor-move">Campo {index + 1} {isSystemField && <span className="text-xs ml-2 px-2 py-0.5 bg-amber/12 text-accent-text rounded">Sistema</span>}</h4>
                          {!isSystemField && (
                            <button
                              type="button"
                              data-tour="form-field-remove"
                              onClick={() => removeField(index)}
                              className="text-error hover:text-error/80"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className='flex items-center gap-2'>
                              <Tooltip placement="right" offset={0} content="Se genera a partir del nombre y es utilizado internamente por el sistema">
                                <InfoIcon size={16} className='text-warning' />
                              </Tooltip>
                              <label className="block text-sm font-medium text-content-muted mb-2">
                                ID del Campo <RequiredIndicator />
                              </label>
                            </div>
                            <input
                              type="text"
                              value={field.name || ''}
                              disabled
                              className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content-muted focus:outline-none cursor-not-allowed disabled:opacity-60"
                              placeholder="Se genera a partir del nombre"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-content-muted mb-2">
                              Nombre del campo <RequiredIndicator />
                            </label>
                            <input
                              type="text"
                              value={field.label}
                              disabled={isSystemField}
                              aria-invalid={showEmptyNameError}
                              onBlur={() => {
                                setTouchedFieldNames((previousTouchedFields) => (
                                  previousTouchedFields.includes(fieldValidationKey)
                                    ? previousTouchedFields
                                    : [...previousTouchedFields, fieldValidationKey]
                                ));
                              }}
                              onChange={(e) => {
                                const newLabel = e.target.value;
                                const newName = generateFieldName(newLabel, index);
                                // Actualizar tanto name como id con el mismo valor
                                updateField(index, { ...field, label: newLabel, name: newName, id: newName });
                              }}
                              className={`w-full px-3 py-2 border rounded-md text-content focus:outline-none focus:ring-2 ${showEmptyNameError ? 'border-error focus:ring-error' : 'border-line focus:ring-amber'} ${isSystemField ? 'bg-surface cursor-not-allowed opacity-70' : 'bg-surface-2'}`}
                              placeholder="Ej: Nombre del cliente"
                              required={true}
                            />
                            {showEmptyNameError && (
                              <label className="text-xs sm:text-sm font-medium text-error mt-1 sm:mb-2">
                                Este campo no puede estar vacío
                              </label>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-content-muted mb-2">
                              Tipo de Campo <RequiredIndicator />
                            </label>
                            <select
                              value={field.type}
                              disabled={isSystemField}
                              onChange={(e) => updateField(index, { ...field, type: e.target.value })}
                              className={`w-full px-3 py-2 border border-line rounded-md text-content focus:outline-none focus:ring-2 focus:ring-amber ${isSystemField ? 'bg-surface cursor-not-allowed opacity-70' : 'bg-surface-2'}`}
                            >
                              {fieldTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-content-muted mb-2">
                              Placeholder
                            </label>
                            <input
                              type="text"
                              value={field.placeholder}
                              disabled={isSystemField}
                              onChange={(e) => updateField(index, { ...field, placeholder: e.target.value })}
                              className={`w-full px-3 py-2 border border-line rounded-md text-content focus:outline-none focus:ring-2 focus:ring-amber ${isSystemField ? 'bg-surface cursor-not-allowed opacity-70' : 'bg-surface-2'}`}
                              placeholder="Texto de ayuda..."
                            />
                          </div>

                          <div className="flex items-center">
                            <label
                              data-tour={isSystemField ? undefined : 'form-field-required'}
                              className={`flex items-center ${isSystemField ? 'cursor-not-allowed opacity-70' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={field.required}
                                disabled={isSystemField}
                                onChange={(e) => updateField(index, { ...field, required: e.target.checked })}
                                className="mr-2"
                              />
                              <span className="text-sm text-content-muted">Campo requerido</span>
                            </label>

                          </div>
                          {(field.type === "select" || field.type === "radio") && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-content-muted mb-2">
                                Opciones (una por línea)
                              </label>
                              <textarea
                                disabled={isSystemField}
                                value={
                                  Array.isArray(field.options)
                                    ? field.options.map((opt) => (typeof opt === "string" ? opt : opt.label)).join("\n")
                                    : ""
                                }
                                onChange={(e) => {
                                  const text = e.target.value
                                  const lines = text.split("\n")
                                  const optionsArray = lines.map((line) => ({
                                    value: line,
                                    label: line,
                                  }))
                                  updateField(index, {
                                    ...field,
                                    options: optionsArray,
                                  })
                                }}
                                rows="4"
                                className={`w-full px-3 py-2 border border-line rounded-md text-content focus:outline-none focus:ring-2 focus:ring-amber ${isSystemField ? 'bg-surface cursor-not-allowed opacity-70' : 'bg-surface-2'}`}
                                placeholder="Opción 1\nOpción 2\nOpción 3"
                              />
                              <p className="mt-1 text-xs text-content-muted">
                                Usa una opción por línea. Se permiten espacios (ej.: "Palabra1 Palabra2").
                              </p>
                            </div>
                          )}
                        </div>
                        {(index === formData.fields.length - 1) && (
                          <div className="flex justify-end items-center">
                            <button
                              type="button"
                              data-tour="form-add-field"
                              onClick={addField}
                              className="px-3 py-1 text-sm bg-success text-white rounded hover:bg-success/80"
                            >
                              + Agregar Campo
                            </button>
                          </div>
                        )}




                      </div>
                    )})}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div data-tour="form-submit" className="sticky bottom-0 z-30 flex justify-end space-x-3 pt-6 border-t border-line bg-surface -mx-6 px-6 pb-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setEditingForm(null);
                    resetFormData();
                  }}
                  className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-line"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="px-6 py-2 bg-amber hover:bg-amber-hover text-on-amber rounded-md "
                  onClick={handleSubmit}
                >
                  {editingForm ? 'Actualizar' : 'Crear'} Formulario

                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingForm && (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-9998 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-line">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-content">
                  {viewingForm.name}
                </h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingForm(null);
                  }}
                  className="text-content-muted hover:text-content"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-content">Información Básica</h3>

                <div>
                  <label className="block text-sm font-medium text-content-muted mb-2">
                    Nombre del Formulario
                  </label>
                  <input
                    type="text"
                    value={viewingForm.name}
                    readOnly
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content-muted cursor-not-allowed opacity-70"
                  />
                </div>

                {viewingForm.description && (
                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={viewingForm.description}
                      readOnly
                      rows="3"
                      className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content-muted cursor-not-allowed opacity-70"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-content-muted mb-2">
                    Tipo de Formulario
                  </label>
                  <input
                    type="text"
                    value={viewingForm.type === 'general' ? 'General' : viewingForm.type === 'finalizacion' ? 'Finalización' : viewingForm.type === 'cancelacion' ? 'Cancelación' : viewingForm.type}
                    readOnly
                    className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content-muted cursor-not-allowed opacity-70"
                  />
                </div>
              </div>

              {/* Task Types */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-content">Tipos de Tarea</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {viewingForm.taskTypes && viewingForm.taskTypes.map((taskType) => (
                    <div key={taskType} className="px-3 py-2 bg-surface-2 text-content-muted rounded text-sm">
                      {taskType}
                    </div>
                  ))}
                  {(!viewingForm.taskTypes || viewingForm.taskTypes.length === 0) && (
                    <p className="text-content-muted text-sm">No hay tipos de tarea asignados</p>
                  )}
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-content">Campos del Formulario</h3>
                {viewingForm.fields && viewingForm.fields.length > 0 ? (
                  <div className="space-y-4">
                    {viewingForm.fields.map((field, index) => (
                      <div key={index} className="border border-line rounded-lg p-4 bg-surface-2">
                        <h4 className="font-medium text-content mb-3">Campo {index + 1}: {field.label}</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-content-muted mb-1">Nombre</label>
                            <input
                              type="text"
                              value={field.name}
                              readOnly
                              className="w-full px-2 py-1 border border-line rounded bg-surface text-content-muted text-sm cursor-not-allowed opacity-70"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-content-muted mb-1">Tipo</label>
                            <input
                              type="text"
                              value={field.type === 'text' ? 'Texto' : field.type === 'textarea' ? 'Área de texto' : field.type === 'number' ? 'Número' : field.type === 'email' ? 'Email' : field.type === 'select' ? 'Selector' : field.type === 'radio' ? 'Botones de radio' : field.type === 'checkbox' ? 'Casilla de verificación' : field.type === 'image' ? 'Imagen' : field.type === 'date' ? 'Fecha' : field.type === 'time' ? 'Hora' : field.type}
                              readOnly
                              className="w-full px-2 py-1 border border-line rounded bg-surface text-content-muted text-sm cursor-not-allowed opacity-70"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-content-muted mb-1">Etiqueta</label>
                            <input
                              type="text"
                              value={field.label}
                              readOnly
                              className="w-full px-2 py-1 border border-line rounded bg-surface text-content-muted text-sm cursor-not-allowed opacity-70"
                            />
                          </div>
                          {field.placeholder && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-content-muted mb-1">Placeholder</label>
                              <input
                                type="text"
                                value={field.placeholder}
                                readOnly
                                className="w-full px-2 py-1 border border-line rounded bg-surface text-content-muted text-sm cursor-not-allowed opacity-70"
                              />
                            </div>
                          )}
                          <div className="col-span-2">
                            <label className="flex items-center text-sm text-content-muted">
                              <input
                                type="checkbox"
                                checked={field.required || false}
                                readOnly
                                className="mr-2 cursor-not-allowed"
                                disabled
                              />
                              Requerido
                            </label>
                          </div>
                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && field.options && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-content-muted mb-1">Opciones</label>
                              <textarea
                                value={Array.isArray(field.options)
                                  ? field.options.map(opt => typeof opt === 'string' ? opt : opt.label || opt.value).join('\n')
                                  : ''}
                                readOnly
                                rows="3"
                                className="w-full px-2 py-1 border border-line rounded bg-surface text-content-muted text-sm cursor-not-allowed opacity-70"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-content-muted">No hay campos en este formulario</p>
                )}
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-6 border-t border-line">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingForm(null);
                  }}
                  className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-line"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Modal */}
      {showDuplicate && (
        <Alerts title={"Duplicar Formulario"} isXVisible={false} >
          <form class="p-6 space-y-6">
            <label className="block text-lg font-medium text-content-muted mb-2">
              Nombre del Formulario *
            </label>
            <input
              type="text"
              value={duplicateForm?.name}
              onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-line rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber"
              placeholder="Nombre del nuevo formulario"
              required
            />
            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-line">
              <button
                type="button"
                onClick={() => {
                  setShowDuplicate(false);
                  setDuplicateForm('');
                }}
                className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-line"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-amber text-on-amber rounded-md hover:bg-amber-hover"
                onClick={() => { handleDuplicateForm(duplicateForm), setShowDuplicate(false), setDuplicateForm('') }}
              >
                Duplicar Formulario
              </button>
            </div>
          </form>
        </Alerts>
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <Alerts title={showAlertsContenidoTitle} isXVisible={false} >
          <div className="p-6">
            <p className="mb-6 text-content-muted">{showAlertsContenidoDelete}</p>
            <div className="flex justify-end space-x-3 pt-6 border-t border-line">
              <button
                type="button"
                onClick={() => {
                  setShowDelete(false);
                  setDeletingForm(null);
                }}
                className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-line"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-error text-white rounded-md hover:bg-error/80"
                onClick={() => { handleDeleteForm(); setShowDelete(false); setDeletingForm(null); }}
              >
                Eliminar Formulario
              </button>
            </div>
          </div>
        </Alerts>
      )}

    </div>
  );
};

export default FormManagement;
